/**
 * Отображение атрибутов конкретного экземпляра предмета: float со шкалой
 * износа, стикеры и брелки.
 *
 * Данные приходят из расширенного прайса Xpanda (/v1/items/full-prices/).
 * Для позиций без них (кейсы, стикеры, наклейки) поля пустые — компоненты
 * это переживают и просто ничего не рисуют.
 */
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';

interface FloatBarProps {
  value: number | null | undefined;
  /** compact — вариант для строки стакана: без подписи, тоньше. */
  compact?: boolean;
}

/**
 * Шкала float 0..1 с маркером. Градиент повторяет принятую в CS2 разбивку
 * по износу, поэтому позицию маркера видно без чтения числа.
 */
export function FloatBar({ value, compact = false }: FloatBarProps) {
  if (value === null || value === undefined) {
    return <span className="text-[#6e7681] text-sm">—</span>;
  }
  // float приходит 0..1, но обрезаем на случай выхода за границы.
  const pct = Math.min(100, Math.max(0, value * 100));

  return (
    <div className={compact ? 'w-full' : 'flex items-center gap-3 w-full'}>
      <div className="relative flex-1 min-w-0">
        <div
          className={`w-full rounded-full ${compact ? 'h-1' : 'h-1.5'}`}
          style={{
            background:
              'linear-gradient(to right, #4b69ff 0%, #4b69ff 7%, #4cd964 7%, ' +
              '#4cd964 15%, #ffd700 15%, #ffd700 38%, #ff8c00 38%, ' +
              '#ff8c00 45%, #eb4b4b 45%, #eb4b4b 100%)',
          }}
        />
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 rounded-full
                     bg-white border border-[#0d1117] shadow"
          style={{
            left: `${pct}%`,
            width: compact ? 7 : 10,
            height: compact ? 7 : 10,
          }}
        />
      </div>
      {!compact && (
        <span className="text-white text-sm font-mono tabular-nums shrink-0">
          {value.toFixed(7)}
        </span>
      )}
    </div>
  );
}

export interface StickerLike {
  name?: string;
  wear?: number | null;
  image_url?: string;
}

interface StickerRowProps {
  stickers?: StickerLike[];
  keychains?: StickerLike[];
  /** Сколько пустых слотов дорисовать до этого числа (в CS2 их 5). */
  slots?: number;
  compact?: boolean;
}

/**
 * Иконки наклеек и брелков. `wear` у Xpanda — доля износа (0.0 = целая),
 * поэтому показываем сохранность как (1 - wear).
 */
export function StickerRow({
  stickers = [],
  keychains = [],
  slots = 0,
  compact = false,
}: StickerRowProps) {
  const items = [...(stickers || []), ...(keychains || [])];
  const empty = Math.max(0, slots - items.length);

  if (items.length === 0 && empty === 0) {
    return <span className="text-[#6e7681] text-sm">—</span>;
  }

  const box = compact ? 'w-8 h-8' : 'w-12 h-12';

  return (
    <div className="flex items-center gap-1 flex-wrap">
      {items.map((s, i) => {
        // wear === null встречается у части позиций — считаем сохранность
        // неизвестной и процент не рисуем, чтобы не выдумывать данные.
        const known = typeof s.wear === 'number';
        const intact = known ? Math.round((1 - (s.wear as number)) * 100) : null;
        return (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <div
                className={`${box} rounded bg-[#0d1117] border border-[#30363d]
                            flex items-center justify-center relative shrink-0`}
              >
                {s.image_url ? (
                  <img
                    src={s.image_url}
                    alt={s.name || 'sticker'}
                    className="max-w-full max-h-full object-contain"
                    loading="lazy"
                    // Часть картинок лежит не на Steam CDN (img.zbt.com и
                    // устаревший steamcdn-a.akamaihd.net). Такие хосты
                    // отдают 200 напрямую, но режут запрос с чужим Referer —
                    // отсюда «стикер есть, а картинки нет».
                    referrerPolicy="no-referrer"
                    onError={(e) => {
                      // Не оставляем пустой квадрат: прячем битую картинку,
                      // сохраняя тултип с названием.
                      const t = e.target as HTMLImageElement;
                      t.onerror = null;
                      t.style.display = 'none';
                      t.parentElement?.classList.add('sticker-img-failed');
                    }}
                  />
                ) : (
                  <span className="text-[#6e7681] text-[10px]">?</span>
                )}
                <span
                  className="hidden [.sticker-img-failed_&]:block text-[#6e7681]
                             text-[9px] leading-none text-center px-0.5"
                >
                  {(s.name || '?').replace(/^Sticker \| /, '').slice(0, 6)}
                </span>
                {intact !== null && !compact && (
                  <span
                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[9px]
                               px-1 rounded bg-[#0d1117] text-[#4cd964] leading-tight"
                  >
                    {intact}%
                  </span>
                )}
              </div>
            </TooltipTrigger>
            <TooltipContent>
              {s.name || 'Наклейка'}
              {intact !== null && ` — ${intact}%`}
            </TooltipContent>
          </Tooltip>
        );
      })}
      {Array.from({ length: empty }).map((_, i) => (
        <div
          key={`e${i}`}
          className={`${box} rounded bg-[#0d1117] border border-dashed border-[#30363d]
                      flex items-center justify-center shrink-0`}
        >
          <span className="text-[#30363d] text-[10px]">Пусто</span>
        </div>
      ))}
    </div>
  );
}

interface ItemAttributesProps {
  floatValue?: number | null;
  paintSeed?: number | null;
  phase?: string | null;
  category?: string | null;
  rarity?: string | null;
  rarityColor?: string | null;
  wear?: string | null;
  nametag?: string | null;
  stickers?: StickerLike[];
  keychains?: StickerLike[];
}

// Ключи в нижнем регистре: в карточку приходит ProductCard.category, а его
// заполняет parsing.get_weapon_category — единственное число и нижний регистр
// («rifle», «knife»), а не «Rifles»/«Knives» из web_routes.get_item_category.
// Словарь знал только вторую форму, поэтому «Тип» показывался по-английски.
// Держим обе.
const CATEGORY_RU: Record<string, string> = {
  rifles: 'Винтовка', pistols: 'Пистолет', smgs: 'Пистолет-пулемёт',
  heavy: 'Тяжёлое', knives: 'Нож', gloves: 'Перчатки',
  container: 'Контейнер', sticker: 'Наклейка', agent: 'Агент',
  'music kit': 'Муз. набор', collectible: 'Коллекционное',
  patch: 'Нашивка', graffiti: 'Граффити', equipment: 'Снаряжение',
  rifle: 'Винтовка', pistol: 'Пистолет', smg: 'Пистолет-пулемёт',
  knife: 'Нож', case: 'Контейнер', other: 'Прочее',
};

const WEAR_RU: Record<string, string> = {
  'Factory New': 'Прямо с завода',
  'Minimal Wear': 'Немного поношенное',
  'Field-Tested': 'После полевых испытаний',
  'Well-Worn': 'Поношенное',
  'Battle-Scarred': 'Закалённое в боях',
};

/** Таблица характеристик экземпляра под блоком «Версия» на карточке товара. */
export function ItemAttributes({
  floatValue, paintSeed, phase, category, rarity, rarityColor, wear,
  nametag, stickers = [], keychains = [],
}: ItemAttributesProps) {
  const rows: { label: string; node: React.ReactNode }[] = [];

  if (floatValue !== null && floatValue !== undefined) {
    rows.push({ label: 'Float', node: <FloatBar value={floatValue} /> });
  }
  if (category) {
    rows.push({
      label: 'Тип',
      node: (
        <span className="text-white text-sm">
          {CATEGORY_RU[category.toLowerCase()] || category}
        </span>
      ),
    });
  }
  if (rarity) {
    rows.push({
      label: 'Редкость',
      node: (
        <span className="flex items-center gap-2 text-white text-sm">
          <span
            className="w-2 h-2 rounded-full shrink-0"
            style={{ backgroundColor: rarityColor || '#6b7280' }}
          />
          {rarity}
        </span>
      ),
    });
  }
  if (wear) {
    rows.push({
      label: 'Износ',
      node: <span className="text-white text-sm">{WEAR_RU[wear] || wear}</span>,
    });
  }
  if (phase) {
    rows.push({ label: 'Фаза', node: <span className="text-white text-sm">{phase}</span> });
  }
  if (paintSeed !== null && paintSeed !== undefined) {
    rows.push({
      label: 'Паттерн',
      node: <span className="text-white text-sm font-mono">#{paintSeed}</span>,
    });
  }
  if (nametag) {
    rows.push({
      label: 'Название',
      node: <span className="text-white text-sm italic">«{nametag}»</span>,
    });
  }

  const hasAccessories = stickers.length > 0 || keychains.length > 0;
  if (rows.length === 0 && !hasAccessories) return null;

  return (
    <div className="bg-[#161b22] rounded-xl border border-[#30363d] overflow-hidden">
      <table className="w-full">
        <tbody className="divide-y divide-[#30363d]">
          {rows.map((r) => (
            <tr key={r.label}>
              <td className="text-[#8b949e] text-sm px-4 py-2.5 w-28 align-middle">
                {r.label}
              </td>
              <td className="px-4 py-2.5">{r.node}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasAccessories && (
        <div className="px-4 py-3 border-t border-[#30363d]">
          <StickerRow stickers={stickers} keychains={keychains} slots={5} />
        </div>
      )}
    </div>
  );
}
